// src/core/map.js
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-rotate";

// Fix default marker icon paths when bundling (Vite)
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const MAP_MODE_KEY = "hydrotools:map-mode";

// CARTO's basemaps.cartocdn.com now requires a paid/registered API key and
// watermarks unauthenticated tiles ("API KEY REQUIRED"). A later fix swapped in
// OpenFreeMap's free vector tiles via maplibre-gl-leaflet, but that plugin's own
// README states it flatly: "No rotation / bearing / pitch support" -- its
// MapLibre canvas never rotates with the rest of the map, so rotating produced a
// hard diagonal seam (the rotated raster/overlay layers cutting across the still
// north-up vector basemap underneath). Esri's free raster Canvas tiles are back
// in use instead: real coverage stops at z16 (deeper zooms upscale a blurry z16
// tile), but unlike the vector option they're plain Leaflet raster tiles and
// rotate correctly along with everything else via leaflet-rotate.
const BASEMAPS = {
  light: { url: "https://server.arcgisonline.com/arcgis/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" },
  dark: { url: "https://server.arcgisonline.com/arcgis/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" },
};

export function createMap({ mapEl, events }) {
  // Set default icon URLs (otherwise markers can appear broken)
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
  });

  const map = L.map(mapEl, {
    zoomControl: true,
    preferCanvas: true,
    maxZoom: 24,
    zoomSnap: 0.25,
    rotate: true,
    bearing: 0,
    rotateControl: false,
    touchRotate: true,
    // Recommended by maplibre-gl-leaflet: MapLibre restricts max latitude more
    // strictly than Leaflet, and zoom 0 has known basemap-sync issues.
    maxBounds: [[180, -Infinity], [-180, Infinity]],
    maxBoundsViscosity: 1,
    minZoom: 1,
    // World view -- no project is active yet at construction time (map.js has no access to
    // the context service; see landing.js), so this should never look like it's pointed at
    // any specific one. The real center/zoom is applied on activation (see
    // onContextMarinaChanged/zoomToCatalogProject in image-generator/index.js).
  }).setView([20, 0], 2);

  let activeBasemapLayer = null;
  const measureLayer = L.featureGroup().addTo(map);
  const measureDraft = L.featureGroup().addTo(map);
  let measureMode = "none"; // none | length | area
  let measureVerts = [];
  let measureControlEl = null;
  let measureStatusEl = null;
  let searchControlEl = null;
  let searchExpanded = false;
  let searchBusy = false;
  let searchSuggestTimer = null;
  let searchSuggesting = false;
  let searchSuggestItems = [];
  let searchSuggestIndex = -1;
  let rotateControlEl = null;
  let northArrowEl = null;
  let mapStatusTrayEl = null;
  let controlStackEl = null;
  let rotateExpanded = false;
  let rotateDialDragging = false;
  let mapillaryControlEl = null;

  // Shared flex-column container every floating map control (theme toggle, capture, measure,
  // search, rotate, mapillary — the last two added by shell.js, which uses this same
  // find-or-create query rather than importing this function) stacks into, instead of each
  // being independently hand-placed with a fixed pixel `top`.
  function ensureControlStack() {
    if (controlStackEl) return controlStackEl;
    controlStackEl = mapEl?.querySelector?.(":scope > .map-control-stack") || null;
    if (!controlStackEl && mapEl) {
      controlStackEl = document.createElement("div");
      controlStackEl.className = "map-control-stack";
      mapEl.appendChild(controlStackEl);
      // The stack sits directly inside the Leaflet map container, so without this,
      // every click on a control button also bubbles up and fires as a map click at
      // that button's screen position -- e.g. activating the measure tool silently
      // planted a first vertex at the button, and the user's actual first map click
      // became the *second* point, instantly drawing a line to nowhere. Match
      // Leaflet's own L.DomEvent.disableClickPropagation/disableScrollPropagation
      // convention for custom controls (no Leaflet dependency needed for this, so
      // shell.js's copy of this same find-or-create function can mirror it too).
      stopMapControlPropagation(controlStackEl);
    }
    return controlStackEl;
  }
  function stopMapControlPropagation(el) {
    ["click", "dblclick", "mousedown", "touchstart", "contextmenu", "wheel"].forEach((type) => {
      el.addEventListener(type, (e) => e.stopPropagation());
    });
  }
  let mapillaryPanelEl = null;
  let mapillaryPickMode = false;
  let mapillaryMarker = null;
  const MAPILLARY_TOKEN_KEY = "hydrotools:mapillary_token";
  let fineWheelAccumulator = 0;
  let fineWheelQueuedSteps = 0;
  let fineWheelFlushTimer = null;
  const US_SURVEY_FEET_PER_METER = 3937 / 1200;

  function formatLength(meters) {
    const m = Number(meters) || 0;
    const ftUs = m * US_SURVEY_FEET_PER_METER;
    if (ftUs < 5280) return `${ftUs.toFixed(1)} ft`;
    return `${(ftUs / 5280).toFixed(3)} mi`;
  }

  function ringAreaSqMeters(latlngs) {
    if (!Array.isArray(latlngs) || latlngs.length < 3) return 0;
    // Spherical excess approximation used by common web mapping tools.
    const R = 6378137;
    let sum = 0;
    for (let i = 0; i < latlngs.length; i += 1) {
      const p1 = latlngs[i];
      const p2 = latlngs[(i + 1) % latlngs.length];
      const lon1 = (p1.lng * Math.PI) / 180;
      const lon2 = (p2.lng * Math.PI) / 180;
      const lat1 = (p1.lat * Math.PI) / 180;
      const lat2 = (p2.lat * Math.PI) / 180;
      sum += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
    }
    return Math.abs((sum * R * R) / 2);
  }

  function formatArea(areaM2) {
    const a = Number(areaM2) || 0;
    const sqFtUs = a * US_SURVEY_FEET_PER_METER * US_SURVEY_FEET_PER_METER;
    const acres = sqFtUs / 43560;
    if (sqFtUs < 43560) return `${sqFtUs.toFixed(1)} sq ft`;
    return `${acres.toFixed(3)} ac`;
  }

  function measureLabelMarker(latlng, text) {
    if (!latlng || !text) return null;
    return L.marker(latlng, {
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: "measure-label-wrap",
        iconSize: [0, 0],
        iconAnchor: [0, 0],
        html: `<div class="measure-label measure-label-inline">${String(text)}</div>`,
      }),
    });
  }

  function midpointLatLng(a, b) {
    if (!a || !b) return null;
    return L.latLng(
      (Number(a.lat) + Number(b.lat)) / 2,
      (Number(a.lng) + Number(b.lng)) / 2,
    );
  }

  function polygonCentroid(latlngs) {
    if (!Array.isArray(latlngs) || latlngs.length < 3) return null;
    const sum = latlngs.reduce((acc, ll) => ({
      lat: acc.lat + Number(ll?.lat || 0),
      lng: acc.lng + Number(ll?.lng || 0),
    }), { lat: 0, lng: 0 });
    return L.latLng(sum.lat / latlngs.length, sum.lng / latlngs.length);
  }

  function sketchLength(latlngs) {
    let m = 0;
    for (let i = 1; i < latlngs.length; i += 1) m += map.distance(latlngs[i - 1], latlngs[i]);
    return m;
  }

  function segmentLength(a, b) {
    if (!a || !b) return 0;
    return map.distance(a, b);
  }

  function addSegmentLengthLabels(group, latlngs) {
    if (!group || !Array.isArray(latlngs) || latlngs.length < 2) return;
    for (let i = 1; i < latlngs.length; i += 1) {
      const start = latlngs[i - 1];
      const end = latlngs[i];
      const mid = midpointLatLng(start, end);
      const label = measureLabelMarker(mid, formatLength(segmentLength(start, end)));
      if (label) label.addTo(group);
    }
  }

  function setMeasureStatus(txt) {
    if (!measureStatusEl) return;
    measureStatusEl.textContent = String(txt || "");
    // Only show the status pill while actually measuring — otherwise it's just a static
    // "Ready" badge sitting in the middle of the map with nothing useful to say.
    measureStatusEl.style.display = measureMode === "none" ? "none" : "";
  }

  function updateMeasureButtons() {
    if (!measureControlEl) return;
    measureControlEl.querySelectorAll("[data-measure-mode]").forEach((btn) => {
      const active = btn.getAttribute("data-measure-mode") === measureMode;
      btn.classList.toggle("active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function clearDraft() {
    measureVerts = [];
    measureDraft.clearLayers();
  }

  function clearMeasurements() {
    clearDraft();
    measureLayer.clearLayers();
    setMeasureStatus("Ready");
  }

  function redrawDraft() {
    measureDraft.clearLayers();
    if (!measureVerts.length || measureMode === "none") return;
    const style = { color: "#22d3ee", weight: 2, opacity: 0.95, dashArray: "5 5" };
    if (measureMode === "length") {
      L.polyline(measureVerts, style).addTo(measureDraft);
      if (measureVerts.length >= 2) {
        const length = sketchLength(measureVerts);
        setMeasureStatus(
          measureVerts.length > 2
            ? `Total length: ${formatLength(length)}`
            : `Length: ${formatLength(length)}`,
        );
        addSegmentLengthLabels(measureDraft, measureVerts);
      } else setMeasureStatus("Length: click to add points, double-click to finish");
    } else if (measureMode === "area") {
      L.polygon(measureVerts, { ...style, fillOpacity: 0.08 }).addTo(measureDraft);
      if (measureVerts.length >= 3) {
        const area = ringAreaSqMeters(measureVerts);
        setMeasureStatus(`Area: ${formatArea(area)}`);
        const centroid = polygonCentroid(measureVerts);
        const label = measureLabelMarker(centroid, formatArea(area));
        if (label) label.addTo(measureDraft);
      } else setMeasureStatus("Area: click to add vertices, double-click to finish");
    }
    measureVerts.forEach((ll) => L.circleMarker(ll, { radius: 3, color: "#22d3ee", weight: 2, fillOpacity: 1 }).addTo(measureDraft));
  }

  function finalizeMeasurement() {
    if (measureMode === "length") {
      if (measureVerts.length < 2) { setMeasureStatus("Need at least 2 points"); clearDraft(); return; }
      const len = sketchLength(measureVerts);
      L.polyline(measureVerts, { color: "#f59e0b", weight: 3, opacity: 0.95 }).addTo(measureLayer);
      addSegmentLengthLabels(measureLayer, measureVerts);
      setMeasureStatus(
        measureVerts.length > 2
          ? `Length saved. Total: ${formatLength(len)}`
          : `Length saved: ${formatLength(len)}`,
      );
      clearDraft();
      return;
    }
    if (measureMode === "area") {
      if (measureVerts.length < 3) { setMeasureStatus("Need at least 3 vertices"); clearDraft(); return; }
      const area = ringAreaSqMeters(measureVerts);
      L.polygon(measureVerts, { color: "#f59e0b", weight: 2.5, fillOpacity: 0.14 }).addTo(measureLayer);
      const centroid = polygonCentroid(measureVerts);
      const label = measureLabelMarker(centroid, formatArea(area));
      if (label) label.addTo(measureLayer);
      setMeasureStatus(`Area saved: ${formatArea(area)}`);
      clearDraft();
    }
  }

  function setMeasureMode(mode) {
    measureMode = mode === "length" || mode === "area" ? mode : "none";
    clearDraft();
    if (measureMode === "none") {
      map.getContainer().style.cursor = "";
      map.doubleClickZoom.enable();
      setMeasureStatus("Ready");
    } else {
      map.getContainer().style.cursor = "crosshair";
      map.doubleClickZoom.disable();
      setMeasureStatus(measureMode === "length" ? "Length: click to add points" : "Area: click to add vertices");
    }
    updateMeasureButtons();
  }

  // ── Mapillary street-level imagery ────────────────────────────────────────

  function mapillaryToken() {
    return localStorage.getItem(MAPILLARY_TOKEN_KEY) || "";
  }

  function closeMapillaryPanel() {
    mapillaryPanelEl?.remove();
    mapillaryPanelEl = null;
    if (mapillaryMarker) { try { map.removeLayer(mapillaryMarker); } catch {} mapillaryMarker = null; }
  }

  function exitMapillaryPickMode() {
    mapillaryPickMode = false;
    mapEl.style.cursor = "";
    map.off("click", onMapillaryMapClick);
    mapillaryControlEl?.querySelector(".map-mapillary-btn")?.classList.remove("active");
  }

  async function fetchNearestImage(lng, lat, token) {
    const url = `https://graph.mapillary.com/images?fields=id,thumb_2048_url,computed_geometry,captured_at&closeto=${lng},${lat}&limit=1&access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Mapillary API ${res.status}`);
    const json = await res.json();
    return json?.data?.[0] || null;
  }

  function haversineM(lat1, lng1, lat2, lng2) {
    const R = 6371000, r = Math.PI / 180;
    const dLat = (lat2 - lat1) * r, dLng = (lng2 - lng1) * r;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function showMapillaryPanel(img, clickLat, clickLng) {
    closeMapillaryPanel();
    const [imgLng, imgLat] = img.computed_geometry?.coordinates || [clickLng, clickLat];
    const dist = haversineM(clickLat, clickLng, imgLat, imgLng);
    const distStr = dist < 1000 ? `${Math.round(dist)} m away` : `${(dist / 1000).toFixed(1)} km away`;
    const date = img.captured_at ? new Date(img.captured_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
    const viewUrl = `https://www.mapillary.com/app/?pKey=${encodeURIComponent(img.id)}`;

    mapillaryPanelEl = document.createElement("div");
    mapillaryPanelEl.className = "map-mapillary-panel";
    mapillaryPanelEl.innerHTML = `
      <div class="map-mapillary-panel-header">
        <span class="map-mapillary-panel-title">Street View</span>
        <span class="map-mapillary-panel-meta">${date ? `${date} · ` : ""}${distStr}</span>
        <button class="map-mapillary-panel-close" title="Close">✕</button>
      </div>
      <a href="${viewUrl}" target="_blank" rel="noopener" class="map-mapillary-img-link">
        <img src="${img.thumb_2048_url}" alt="Street-level image" class="map-mapillary-img" loading="lazy">
        <div class="map-mapillary-img-overlay">Open in Mapillary ↗</div>
      </a>
      <div class="map-mapillary-panel-footer">
        <a href="https://www.mapillary.com" target="_blank" rel="noopener" class="map-mapillary-credit">Powered by Mapillary</a>
        <a href="${viewUrl}" target="_blank" rel="noopener" class="map-mapillary-view-link">Full viewer ↗</a>
      </div>`;
    mapillaryPanelEl.querySelector(".map-mapillary-panel-close").addEventListener("click", closeMapillaryPanel);
    mapEl.parentElement.appendChild(mapillaryPanelEl);

    // Drop a marker at the image location
    if (imgLat && imgLng) {
      mapillaryMarker = L.circleMarker([imgLat, imgLng], {
        radius: 6, color: "#1db954", fillColor: "#1db954", fillOpacity: 0.85, weight: 2,
      }).addTo(map);
    }
  }

  async function onMapillaryMapClick(e) {
    if (!mapillaryPickMode) return;
    exitMapillaryPickMode();
    const token = mapillaryToken();
    if (!token) return;
    const { lat, lng } = e.latlng;

    closeMapillaryPanel();
    // Show a temporary loading panel
    mapillaryPanelEl = document.createElement("div");
    mapillaryPanelEl.className = "map-mapillary-panel map-mapillary-loading";
    mapillaryPanelEl.innerHTML = `<div class="map-mapillary-panel-header"><span class="map-mapillary-panel-title">Street View</span><button class="map-mapillary-panel-close" title="Close">✕</button></div><div style="padding:12px;color:var(--muted);font-size:11px;">Searching nearby images…</div>`;
    mapillaryPanelEl.querySelector(".map-mapillary-panel-close").addEventListener("click", closeMapillaryPanel);
    mapEl.parentElement.appendChild(mapillaryPanelEl);

    try {
      const img = await fetchNearestImage(lng, lat, token);
      if (!img) {
        if (mapillaryPanelEl) mapillaryPanelEl.innerHTML = `<div class="map-mapillary-panel-header"><span class="map-mapillary-panel-title">Street View</span><button class="map-mapillary-panel-close" title="Close">✕</button></div><div style="padding:12px;color:var(--muted);font-size:11px;">No Mapillary imagery found near this location.</div>`;
        mapillaryPanelEl?.querySelector(".map-mapillary-panel-close")?.addEventListener("click", closeMapillaryPanel);
        return;
      }
      showMapillaryPanel(img, lat, lng);
    } catch (err) {
      if (mapillaryPanelEl) mapillaryPanelEl.innerHTML = `<div class="map-mapillary-panel-header"><span class="map-mapillary-panel-title">Street View</span><button class="map-mapillary-panel-close" title="Close">✕</button></div><div style="padding:12px;color:#f87171;font-size:11px;">Error: ${err.message || "Request failed"}</div>`;
      mapillaryPanelEl?.querySelector(".map-mapillary-panel-close")?.addEventListener("click", closeMapillaryPanel);
    }
  }

  function renderMapillarySetup() {
    if (!mapillaryControlEl) return;
    const setupEl = mapillaryControlEl.querySelector(".map-mapillary-setup");
    if (!setupEl) return;
    const token = mapillaryToken();
    if (token) {
      setupEl.style.display = "none";
    } else {
      setupEl.style.display = "block";
    }
  }

  function ensureMapillaryControl() {
    if (!mapEl || mapillaryControlEl) return;
    mapillaryControlEl = document.createElement("div");
    mapillaryControlEl.className = "map-mapillary-control";
    mapillaryControlEl.innerHTML = `
      <button type="button" class="map-measure-btn map-mapillary-btn" title="Street View (Mapillary)" aria-label="Street View">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </button>
      <div class="map-mapillary-setup">
        <div class="map-mapillary-setup-card">
          <div style="font-weight:700;font-size:11px;margin-bottom:6px;">Mapillary API Token</div>
          <div style="font-size:10px;color:var(--muted);margin-bottom:6px;">Free token from <a href="https://www.mapillary.com/developer" target="_blank" rel="noopener" style="color:var(--accent);">mapillary.com/developer</a></div>
          <input id="map-mapillary-token-input" type="password" placeholder="Paste token…" style="width:100%;box-sizing:border-box;font-size:10px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg2);color:var(--fg);">
          <button id="map-mapillary-token-save" style="margin-top:5px;width:100%;font-size:10px;padding:3px 0;border-radius:4px;border:1px solid var(--accent);background:transparent;color:var(--accent);cursor:pointer;">Save</button>
        </div>
      </div>`;

    const btn = mapillaryControlEl.querySelector(".map-mapillary-btn");
    const setup = mapillaryControlEl.querySelector(".map-mapillary-setup");
    const tokenInput = mapillaryControlEl.querySelector("#map-mapillary-token-input");
    const tokenSave = mapillaryControlEl.querySelector("#map-mapillary-token-save");

    btn.addEventListener("click", () => {
      if (!mapillaryToken()) {
        setup.style.display = setup.style.display === "none" ? "block" : "none";
        return;
      }
      if (mapillaryPickMode) {
        exitMapillaryPickMode();
        closeMapillaryPanel();
      } else {
        closeMapillaryPanel();
        mapillaryPickMode = true;
        mapEl.style.cursor = "crosshair";
        map.on("click", onMapillaryMapClick);
        btn.classList.add("active");
      }
    });

    tokenSave.addEventListener("click", () => {
      const val = tokenInput.value.trim();
      if (!val) return;
      localStorage.setItem(MAPILLARY_TOKEN_KEY, val);
      setup.style.display = "none";
      tokenInput.value = "";
    });

    renderMapillarySetup();
    ensureControlStack()?.appendChild(mapillaryControlEl);
  }

  // ──────────────────────────────────────────────────────────────────────────

  function ensureMeasureControl() {
    if (!mapEl || measureControlEl) return;
    measureControlEl = document.createElement("div");
    measureControlEl.className = "map-measure-control";
    measureControlEl.innerHTML = `
      <button type="button" class="map-measure-btn" data-measure-mode="length" title="Measure length" aria-label="Measure length">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M6 18 18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
          <circle cx="6" cy="18" r="2.1" fill="currentColor"/>
          <circle cx="18" cy="6" r="2.1" fill="currentColor"/>
          <path d="M10.2 13.8h2.1M12.1 11.9v2.1M13.8 10.2h2.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>
        </svg>
      </button>
      <button type="button" class="map-measure-btn map-measure-area-btn" data-measure-mode="area" title="Measure area" aria-label="Measure area">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M6 15 9 6.5 17.5 7 19 14.5 12 18Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" fill="none"/>
          <circle cx="6" cy="15" r="1.5" fill="currentColor"/>
          <circle cx="9" cy="6.5" r="1.5" fill="currentColor"/>
          <circle cx="17.5" cy="7" r="1.5" fill="currentColor"/>
          <circle cx="19" cy="14.5" r="1.5" fill="currentColor"/>
          <circle cx="12" cy="18" r="1.5" fill="currentColor"/>
        </svg>
      </button>
      <button type="button" class="map-measure-btn" data-measure-clear="1" title="Clear measurements" aria-label="Clear measurements">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41 17.59 5 12 10.59 6.41 5Z"/></svg>
      </button>
      <div id="map-measure-status" class="map-measure-status" aria-live="polite"></div>
    `;
    measureControlEl.addEventListener("click", (e) => {
      const modeBtn = e.target?.closest?.("[data-measure-mode]");
      if (modeBtn) {
        const mode = modeBtn.getAttribute("data-measure-mode");
        setMeasureMode(measureMode === mode ? "none" : mode);
        return;
      }
      if (e.target?.closest?.("[data-measure-clear]")) {
        clearMeasurements();
      }
    });
    ensureControlStack()?.appendChild(measureControlEl);
    measureStatusEl = measureControlEl.querySelector("#map-measure-status");
    updateMeasureButtons();
    setMeasureStatus("Ready");
  }

  function setSearchExpanded(next) {
    searchExpanded = !!next;
    if (!searchControlEl) return;
    searchControlEl.dataset.expanded = searchExpanded ? "true" : "false";
    const input = searchControlEl.querySelector("#map-search-input");
    if (searchExpanded) {
      setTimeout(() => {
        input?.focus?.();
        input?.select?.();
      }, 40);
    }
  }

  function setSearchBusy(next, status = "") {
    searchBusy = !!next;
    if (!searchControlEl) return;
    searchControlEl.dataset.busy = searchBusy ? "true" : "false";
    const statusEl = searchControlEl.querySelector("#map-search-status");
    if (statusEl) statusEl.textContent = status || "";
  }

  function parseNominatimBounds(row) {
    const b = Array.isArray(row?.boundingbox) ? row.boundingbox : null;
    if (!b || b.length < 4) return null;
    const south = Number(b[0]);
    const north = Number(b[1]);
    const west = Number(b[2]);
    const east = Number(b[3]);
    if (![south, north, west, east].every(Number.isFinite)) return null;
    const bounds = L.latLngBounds([south, west], [north, east]);
    return bounds.isValid() ? bounds : null;
  }

  function parsePhotonBounds(feature) {
    const extent = Array.isArray(feature?.properties?.extent) ? feature.properties.extent : null;
    if (!extent || extent.length < 4) return null;
    const west = Number(extent[0]);
    const south = Number(extent[1]);
    const east = Number(extent[2]);
    const north = Number(extent[3]);
    if (![south, north, west, east].every(Number.isFinite)) return null;
    const bounds = L.latLngBounds([south, west], [north, east]);
    return bounds.isValid() ? bounds : null;
  }

  function clearSearchSuggestions() {
    searchSuggestItems = [];
    searchSuggestIndex = -1;
    const list = searchControlEl?.querySelector?.("#map-search-suggestions");
    if (list) {
      list.innerHTML = "";
      list.hidden = true;
    }
  }

  function renderSearchSuggestions(items) {
    searchSuggestItems = Array.isArray(items) ? items : [];
    searchSuggestIndex = -1;
    const list = searchControlEl?.querySelector?.("#map-search-suggestions");
    if (!list) return;
    if (!searchSuggestItems.length) {
      list.innerHTML = "";
      list.hidden = true;
      return;
    }
    list.innerHTML = searchSuggestItems
      .map((item, idx) => `<button type="button" class="map-search-suggestion" data-suggest-index="${idx}" title="${String(item.label || "").replace(/"/g, "&quot;")}">${item.label}</button>`)
      .join("");
    list.hidden = false;
  }

  function updateSearchSuggestionHighlight() {
    const list = searchControlEl?.querySelector?.("#map-search-suggestions");
    if (!list) return;
    list.querySelectorAll(".map-search-suggestion").forEach((el, idx) => {
      el.classList.toggle("active", idx === searchSuggestIndex);
    });
  }

  function zoomToSearchResult(result) {
    if (!result) return;
    if (result?.bounds) map.fitBounds(result.bounds.pad(0.2));
    else if (Number.isFinite(result?.lat) && Number.isFinite(result?.lon)) map.setView([result.lat, result.lon], Math.max(map.getZoom(), 12));
  }

  async function suggestPlaces(query) {
    const q = String(query || "").trim();
    if (q.length < 2) return [];
    const encoded = encodeURIComponent(q);
    const timeoutMs = 5000;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const url = `https://photon.komoot.io/api/?q=${encoded}&limit=5`;
      const r = await fetch(url, { signal: ctl.signal, headers: { Accept: "application/json" } });
      if (!r.ok) return [];
      const data = await r.json();
      const feats = Array.isArray(data?.features) ? data.features : [];
      return feats.map((ft) => {
        const c = ft?.geometry?.coordinates;
        const lon = Number(Array.isArray(c) ? c[0] : NaN);
        const lat = Number(Array.isArray(c) ? c[1] : NaN);
        const name = String(ft?.properties?.name || "").trim();
        const city = String(ft?.properties?.city || ft?.properties?.state || ft?.properties?.country || "").trim();
        const label = city && city !== name ? `${name}, ${city}` : name || q;
        return {
          lat,
          lon,
          label,
          bounds: parsePhotonBounds(ft),
        };
      }).filter((it) => Number.isFinite(it.lat) && Number.isFinite(it.lon));
    } catch {
      return [];
    } finally {
      clearTimeout(timer);
    }
  }

  async function geocodePlace(query) {
    const q = String(query || "").trim();
    if (!q) return null;
    const encoded = encodeURIComponent(q);
    const timeoutMs = 10000;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const primary = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encoded}`;
      const r1 = await fetch(primary, { signal: ctl.signal, headers: { Accept: "application/json" } });
      if (r1.ok) {
        const rows = await r1.json();
        if (Array.isArray(rows) && rows.length) {
          const row = rows[0];
          const lat = Number(row.lat);
          const lon = Number(row.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            return {
              lat,
              lon,
              label: String(row.display_name || q),
              bounds: parseNominatimBounds(row),
            };
          }
        }
      }
    } catch {}
    try {
      const fallback = `https://photon.komoot.io/api/?q=${encoded}&limit=1`;
      const r2 = await fetch(fallback, { signal: ctl.signal, headers: { Accept: "application/json" } });
      if (r2.ok) {
        const data = await r2.json();
        const ft = Array.isArray(data?.features) ? data.features[0] : null;
        const c = ft?.geometry?.coordinates;
        const lon = Number(Array.isArray(c) ? c[0] : NaN);
        const lat = Number(Array.isArray(c) ? c[1] : NaN);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return {
            lat,
            lon,
            label: String(ft?.properties?.name || q),
            bounds: null,
          };
        }
      }
    } catch {}
    finally {
      clearTimeout(timer);
    }
    return null;
  }

  function ensureSearchControl() {
    if (!mapEl || searchControlEl) return;
    searchControlEl = document.createElement("div");
    searchControlEl.className = "map-search-control";
    searchControlEl.dataset.expanded = "false";
    searchControlEl.dataset.busy = "false";
    searchControlEl.innerHTML = `
      <div class="map-search-shell">
        <button type="button" id="map-search-toggle" class="map-search-btn" title="Search place" aria-label="Search place" aria-expanded="false">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16a6.47 6.47 0 0 0 4.23-1.57l.27.28v.79L20 21.5 21.5 20l-6-6Zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14Z"/></svg>
        </button>
        <form id="map-search-form" class="map-search-form" autocomplete="off">
          <input id="map-search-input" type="text" placeholder="Search place..." />
          <div id="map-search-suggestions" class="map-search-suggestions" hidden></div>
        </form>
      </div>
      <div id="map-search-status" class="map-search-status" aria-live="polite"></div>
    `;

    searchControlEl.addEventListener("click", (e) => {
      const toggle = e.target?.closest?.("#map-search-toggle");
      if (!toggle) return;
      const next = !searchExpanded;
      setSearchExpanded(next);
      toggle.setAttribute("aria-expanded", next ? "true" : "false");
      if (!next) {
        setSearchBusy(false, "");
        clearSearchSuggestions();
      }
    });

    const form = searchControlEl.querySelector("#map-search-form");
    const input = searchControlEl.querySelector("#map-search-input");
    form?.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (searchSuggestIndex >= 0 && searchSuggestItems[searchSuggestIndex]) {
        const picked = searchSuggestItems[searchSuggestIndex];
        zoomToSearchResult(picked);
        setSearchBusy(false, picked.label);
        input.value = picked.label;
        clearSearchSuggestions();
        return;
      }
      const query = String(input?.value || "").trim();
      if (!query || searchBusy) return;
      setSearchBusy(true, "Searching...");
      const result = await geocodePlace(query);
      zoomToSearchResult(result);
      setSearchBusy(false, result ? result.label : "No results.");
      clearSearchSuggestions();
    });

    input?.addEventListener("input", () => {
      const q = String(input?.value || "").trim();
      if (searchSuggestTimer) clearTimeout(searchSuggestTimer);
      if (!q || q.length < 2) {
        clearSearchSuggestions();
        return;
      }
      searchSuggestTimer = setTimeout(async () => {
        if (searchSuggesting) return;
        searchSuggesting = true;
        const items = await suggestPlaces(q);
        searchSuggesting = false;
        const current = String(input?.value || "").trim();
        if (current !== q || !searchExpanded) return;
        renderSearchSuggestions(items);
      }, 220);
    });

    input?.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" && searchSuggestItems.length) {
        e.preventDefault();
        searchSuggestIndex = Math.min(searchSuggestItems.length - 1, searchSuggestIndex + 1);
        updateSearchSuggestionHighlight();
        return;
      }
      if (e.key === "ArrowUp" && searchSuggestItems.length) {
        e.preventDefault();
        searchSuggestIndex = Math.max(0, searchSuggestIndex - 1);
        updateSearchSuggestionHighlight();
        return;
      }
      if (e.key === "Enter" && searchSuggestIndex >= 0 && searchSuggestItems[searchSuggestIndex]) {
        e.preventDefault();
        const picked = searchSuggestItems[searchSuggestIndex];
        input.value = picked.label;
        zoomToSearchResult(picked);
        setSearchBusy(false, picked.label);
        clearSearchSuggestions();
        return;
      }
      if (e.key === "Escape") {
        setSearchExpanded(false);
        searchControlEl.querySelector("#map-search-toggle")?.setAttribute("aria-expanded", "false");
        setSearchBusy(false, "");
        clearSearchSuggestions();
      }
    });

    searchControlEl.addEventListener("mousedown", (e) => {
      const btn = e.target?.closest?.(".map-search-suggestion");
      if (!btn) return;
      e.preventDefault();
      const idx = Number(btn.getAttribute("data-suggest-index"));
      if (!Number.isFinite(idx) || !searchSuggestItems[idx]) return;
      const picked = searchSuggestItems[idx];
      input.value = picked.label;
      zoomToSearchResult(picked);
      setSearchBusy(false, picked.label);
      clearSearchSuggestions();
    });

    document.addEventListener("mousedown", (e) => {
      if (!searchExpanded) return;
      if (searchControlEl?.contains?.(e.target)) return;
      setSearchExpanded(false);
      searchControlEl.querySelector("#map-search-toggle")?.setAttribute("aria-expanded", "false");
      setSearchBusy(false, "");
      clearSearchSuggestions();
    });

    ensureControlStack()?.appendChild(searchControlEl);
  }

  function getBearingDeg() {
    const raw = Number(map?.getBearing?.());
    if (!Number.isFinite(raw)) return 0;
    const norm = raw % 360;
    return norm < 0 ? norm + 360 : norm;
  }

  function setBearingDeg(next) {
    if (typeof map?.setBearing !== "function") return;
    map.setBearing(Number(next) || 0);
    updateRotateUI();
  }

  function setRotateExpanded(next) {
    rotateExpanded = !!next;
    if (!rotateControlEl) return;
    rotateControlEl.dataset.expanded = rotateExpanded ? "true" : "false";
    const toggle = rotateControlEl.querySelector("#map-rotate-toggle");
    if (toggle) {
      toggle.setAttribute("aria-expanded", rotateExpanded ? "true" : "false");
      toggle.classList.toggle("active", rotateExpanded);
    }
  }

  function updateRotateUI() {
    if (!rotateControlEl) return;
    const bearing = getBearingDeg();
    const zoom = Number(map?.getZoom?.());
    const status = mapStatusTrayEl?.querySelector("#map-rotate-status");
    const zoomStatus = mapStatusTrayEl?.querySelector("#map-zoom-status");
    if (status) status.textContent = `${bearing.toFixed(0)} deg`;
    if (zoomStatus) zoomStatus.textContent = Number.isFinite(zoom) ? `Z${zoom.toFixed(1)}` : "Z?";
    const arrow = northArrowEl?.querySelector?.(".map-north-arrow-pointer");
    if (arrow) arrow.style.transform = `rotate(${bearing}deg)`;
    if (!rotateDialDragging) {
      const needle = rotateControlEl.querySelector(".map-rotate-dial-needle");
      if (needle) needle.style.transform = `rotate(${bearing}deg)`;
    }
  }

  // Scoped to mapEl itself (not mapEl.parentElement) so it stays within the map's own box in
  // every view mode, including the split view's 50%-width map pane, instead of floating over
  // whatever sits to the right of it.
  function ensureNorthArrow() {
    if (!mapEl || northArrowEl) return;
    northArrowEl = document.createElement("div");
    northArrowEl.className = "map-north-arrow";
    northArrowEl.setAttribute("aria-label", "North arrow");
    northArrowEl.innerHTML = `
      <div class="map-north-arrow-pointer">
        <svg viewBox="0 0 32 48" focusable="false" aria-hidden="true">
          <path d="M16 3 27 33 16 27 5 33Z" fill="currentColor"/>
          <path d="M16 3v24" stroke="rgba(2,6,23,.72)" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <span>N</span>
      </div>
    `;
    mapEl.appendChild(northArrowEl);
  }

  // Bottom status tray — same visual language as #sys-bar (the app's top system bar), but
  // scoped to the map's own box so it narrows correctly in split view. Houses the zoom-level
  // and heading readouts, which used to live inside the rotate control and visually overlapped
  // the north arrow.
  function ensureMapStatusTray() {
    if (!mapEl || mapStatusTrayEl) return;
    mapStatusTrayEl = document.createElement("div");
    mapStatusTrayEl.className = "map-status-tray";
    mapStatusTrayEl.innerHTML = `
      <span id="map-zoom-status" class="map-status-tray-item" aria-live="polite">Z?</span>
      <span class="map-status-tray-sep" aria-hidden="true">|</span>
      <span id="map-rotate-status" class="map-status-tray-item" aria-live="polite">0 deg</span>
      <span id="map-status-tray-extra" class="map-status-tray-extra"></span>
      <span id="map-status-tray-right" class="map-status-tray-right"></span>
    `;
    mapEl.appendChild(mapStatusTrayEl);

    // The tray itself is pointer-events:none (an overlay that shouldn't block map drag/click
    // underneath it), but map-status-tray-right opts back into pointer-events for its own
    // interactive content (e.g. slip-delivery's color-by control) -- see its CSS rule. Content
    // there is tool-authored markup injected via setStatusTrayRight, not user-typed text, so a
    // single delegated listener re-emitting [data-tray-action] elements is enough; the tool
    // that provided the markup is the one that knows what each action means, so this only
    // forwards the interaction rather than interpreting it.
    mapStatusTrayEl.addEventListener("click", (e) => {
      const target = e.target.closest("[data-tray-action]");
      if (!target || !mapStatusTrayEl.contains(target)) return;
      events.emit("map:status:tray-right:action", { action: target.dataset.trayAction, value: target.value });
    });
    mapStatusTrayEl.addEventListener("change", (e) => {
      const target = e.target.closest("[data-tray-action]");
      if (!target || !mapStatusTrayEl.contains(target)) return;
      events.emit("map:status:tray-right:action", { action: target.dataset.trayAction, value: target.value });
    });
  }

  // Lets a tool (e.g. slip-delivery's selection metrics) append its own read-outs to the
  // tray without knowing about the zoom/rotate items already there — set via the
  // "map:status:extra" event instead of a direct API since the map instance itself is what
  // tools hold a reference to (see createMap's return value), not this closure. Plain
  // textContent per item, not innerHTML: item text comes from tool-computed numbers, but
  // there's no reason to trust it as markup.
  function setStatusTrayExtra(items) {
    if (!mapStatusTrayEl) return;
    const el = mapStatusTrayEl.querySelector("#map-status-tray-extra");
    if (!el) return;
    el.innerHTML = "";
    const list = Array.isArray(items) ? items.filter((text) => text != null && String(text) !== "") : [];
    list.forEach((text) => {
      const sep = document.createElement("span");
      sep.className = "map-status-tray-sep";
      sep.setAttribute("aria-hidden", "true");
      sep.textContent = "|";
      el.appendChild(sep);
      const item = document.createElement("span");
      item.className = "map-status-tray-item";
      item.setAttribute("aria-live", "polite");
      item.textContent = String(text);
      el.appendChild(item);
    });
  }

  // Right-justified counterpart to setStatusTrayExtra, for a tool's own interactive controls
  // (e.g. slip-delivery's color-by select + cycle arrows) rather than plain read-out text.
  // Set via the "map:status:tray-right" event, same reasoning as setStatusTrayExtra above --
  // interaction on the injected markup comes back out via "map:status:tray-right:action"
  // (delegated in ensureMapStatusTray), so the calling tool never needs a direct DOM reference.
  function setStatusTrayRight(html) {
    if (!mapStatusTrayEl) return;
    const el = mapStatusTrayEl.querySelector("#map-status-tray-right");
    if (!el) return;
    el.innerHTML = html || "";
  }

  function ensureRotateControl() {
    if (!mapEl || rotateControlEl) return;
    rotateControlEl = document.createElement("div");
    rotateControlEl.className = "map-rotate-control";
    rotateControlEl.dataset.expanded = "false";
    rotateControlEl.innerHTML = `
      <div class="map-rotate-shell">
        <button type="button" id="map-rotate-toggle" class="map-measure-btn map-rotate-btn" title="Rotate map" aria-label="Rotate map" aria-expanded="false">
          <svg class="map-rotate-icon" viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M12 2v3.2a6.8 6.8 0 1 1-6.8 6.8H2a10 10 0 1 0 10-10Zm-1.8.6L6.8 6h2.6v4h2.2V6h2.6l-3.4-3.4Z"/></svg>
          <div class="map-rotate-dial-tick-n">N</div>
          <div class="map-rotate-dial-needle"></div>
          <div class="map-rotate-dial-hub"></div>
        </button>
        <button type="button" class="map-measure-btn map-rotate-btn map-rotate-reset" data-rotate-action="reset" title="Reset north-up" aria-label="Reset north-up">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="m12 2 6 6h-4v6h-4V8H6l6-6Zm-7 15h14v2H5v-2Z"/></svg>
        </button>
      </div>
    `;

    // The toggle button IS the dial -- clicking it while collapsed grows/morphs it in place
    // (width/height + icon-to-needle crossfade, all CSS-transitioned) instead of sliding a
    // separate panel out below it; a second click elsewhere (or Escape) reverses the same
    // transition back to a plain small button. Once expanded, dragging the same element
    // rotates the map -- same screen-space-angle-from-the-button's-own-center approach used
    // for slip-delivery's per-polygon rotate dial.
    const toggle = rotateControlEl.querySelector("#map-rotate-toggle");
    const needle = rotateControlEl.querySelector(".map-rotate-dial-needle");
    if (toggle && needle) {
      const applyFromPointer = (clientX, clientY) => {
        const rect = toggle.getBoundingClientRect();
        const dx = clientX - (rect.left + rect.width / 2);
        const dy = clientY - (rect.top + rect.height / 2);
        const raw = (Math.atan2(dx, -dy) * 180) / Math.PI;
        const snapped = Math.round(raw < 0 ? raw + 360 : raw);
        setBearingDeg(snapped);
        needle.style.transform = `rotate(${snapped}deg)`;
        const status = mapStatusTrayEl?.querySelector("#map-rotate-status");
        if (status) status.textContent = `${snapped} deg`;
      };
      const onPointerDown = (e) => {
        if (e.button != null && e.button !== 0) return;
        e.preventDefault();
        if (!rotateExpanded) {
          // First click just opens it and lets the grow animation play -- doesn't also try
          // to read a rotate gesture off a click against the button while it's still tiny.
          setRotateExpanded(true);
          return;
        }
        rotateDialDragging = true;
        needle.style.transition = "none";
        toggle.classList.add("dragging");
        try { toggle.setPointerCapture?.(e.pointerId); } catch {}
        applyFromPointer(e.clientX, e.clientY);
      };
      const onPointerMove = (e) => {
        if (!rotateDialDragging) return;
        applyFromPointer(e.clientX, e.clientY);
      };
      const onPointerUp = () => {
        if (!rotateDialDragging) return;
        rotateDialDragging = false;
        needle.style.transition = "";
        toggle.classList.remove("dragging");
      };
      const onWheel = (e) => {
        if (!rotateExpanded) return;
        e.preventDefault();
        const dir = e.deltaY > 0 ? 1 : -1;
        const next = Math.round(getBearingDeg() + dir);
        setBearingDeg(((next % 360) + 360) % 360);
      };
      toggle.addEventListener("pointerdown", onPointerDown);
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
      toggle.addEventListener("wheel", onWheel, { passive: false });
      toggle.addEventListener("dblclick", (e) => {
        if (!rotateExpanded) return;
        e.preventDefault();
        setBearingDeg(0);
      });
    }

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !rotateExpanded) return;
      rotateDialDragging = false;
      if (needle) needle.style.transition = "";
      toggle?.classList.remove("dragging");
      setRotateExpanded(false);
    });

    rotateControlEl.querySelector("[data-rotate-action=\"reset\"]")?.addEventListener("click", () => {
      setBearingDeg(0);
    });

    document.addEventListener("mousedown", (e) => {
      if (!rotateExpanded) return;
      if (rotateControlEl?.contains?.(e.target)) return;
      setRotateExpanded(false);
    });

    map.on("rotate", updateRotateUI);
    map.on("zoom zoomend", updateRotateUI);
    ensureControlStack()?.appendChild(rotateControlEl);
    updateRotateUI();
  }

  function applyFineWheelZoom(event) {
    const deltaY = Number(event?.deltaY || 0);
    if (!Number.isFinite(deltaY) || deltaY === 0) return;
    const scale = event.deltaMode === 1 ? 18 : event.deltaMode === 2 ? 120 : 1;
    fineWheelAccumulator += deltaY * scale;
    const threshold = 120;
    if (Math.abs(fineWheelAccumulator) < threshold) return;
    const direction = fineWheelAccumulator > 0 ? -1 : 1;
    fineWheelAccumulator = 0;
    fineWheelQueuedSteps += direction;
    const rect = map.getContainer().getBoundingClientRect();
    const point = L.point(
      Number(event.clientX) - rect.left,
      Number(event.clientY) - rect.top,
    );
    if (fineWheelFlushTimer) clearTimeout(fineWheelFlushTimer);
    fineWheelFlushTimer = setTimeout(() => {
      const queued = fineWheelQueuedSteps;
      fineWheelQueuedSteps = 0;
      fineWheelFlushTimer = null;
      if (!queued) return;
      const currentZoom = Number(map.getZoom?.() || 0);
      const nextZoom = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), currentZoom + (queued * 0.25)));
      map.setZoomAround(point, nextZoom, { animate: false });
    }, 90);
  }

  map.on("click", (e) => {
    if (measureMode === "none") return;
    measureVerts.push(e.latlng);
    redrawDraft();
  });

  map.on("dblclick", (e) => {
    if (measureMode === "none") return;
    L.DomEvent.stop(e);
    finalizeMeasurement();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (measureMode === "none") return;
    clearDraft();
    setMeasureStatus("Sketch cancelled");
  });

  map.getContainer().addEventListener("wheel", (e) => {
    if (!e.ctrlKey) {
      fineWheelAccumulator = 0;
      fineWheelQueuedSteps = 0;
      if (fineWheelFlushTimer) {
        clearTimeout(fineWheelFlushTimer);
        fineWheelFlushTimer = null;
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    applyFineWheelZoom(e);
  }, { passive: false, capture: true });

  function setBasemap(mode) {
    const nextMode = mode === "dark" ? "dark" : "light";
    const def = BASEMAPS[nextMode];
    if (!def) return;
    if (activeBasemapLayer) map.removeLayer(activeBasemapLayer);
    activeBasemapLayer = L.tileLayer(def.url, {
      maxNativeZoom: 16,
      maxZoom: 24,
      attribution: "Tiles &copy; Esri",
    }).addTo(map);
  }

  const savedMode = localStorage.getItem(MAP_MODE_KEY);
  setBasemap(savedMode === "light" ? "light" : "dark");
  ensureMeasureControl();
  ensureSearchControl();
  ensureNorthArrow();
  ensureMapStatusTray();
  ensureRotateControl();
  ensureMapillaryControl();

  events.on("map:basemap:mode", ({ mode } = {}) => {
    setBasemap(mode);
  });

  events.on("map:status:extra", ({ items } = {}) => {
    setStatusTrayExtra(items);
  });

  events.on("map:status:tray-right", ({ html } = {}) => {
    setStatusTrayRight(html);
  });

  // If the container was sized after init, Leaflet needs invalidateSize()
  setTimeout(() => map.invalidateSize(), 200);
  window.addEventListener("resize", () => {
    requestAnimationFrame(() => map.invalidateSize({ pan: false, debounceMoveend: true }));
  });

  events.emit("map:ready", { map });
  return map;
}



